import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "@sentry/react";

/**
 * Keeps a render fault in one part of the app from taking the rest down with
 * it. Every boundary in the product is this one: what stands in the part's
 * place and what makes it worth another try are its owner's to say.
 *
 * Without a boundary above it, anything thrown while a component renders or
 * commits reaches the root and React unmounts the whole tree. The rail showed
 * what that costs: it is on every page, it draws what the server sends, and
 * the page and the service deploy separately while a tab stays open for days.
 * So an answer the page was not built for threw the moment it arrived, and the
 * user was left a blank window without having clicked anything (an issue kind
 * the sync indicator had no words for, 4 Oct 2026).
 *
 * **Where they stand**, innermost first, each catching what the ones inside it
 * let through (a fallback that throws included):
 *
 * - What a rail fixture draws from the server: the sync indicator's chip
 *   (`PmsSyncStatus`) and, inside it, what the chip says of tonight (its
 *   third line, its tooltip's sentences and its popover's section cost only
 *   themselves), the property switcher and the billing chip (`Sidebar`),
 *   what is waiting with Optivo (`AskOptivoRail`) and the request window
 *   (`AskOptivoProvider`). A fault there costs that fixture, and the
 *   navigation beside it goes on working.
 * - A page (`PageErrorBoundary`), which leaves the rail to navigate away with.
 * - The notice that takes a page's place while another property is open than
 *   the tab's (`App`). It names both properties from a list it reads itself,
 *   so the notice without the names stands in, and the page kept hidden under
 *   it stays, with its unsaved work.
 * - The rail as a whole (`App`), for what is left of it: the navigation, the
 *   footer, a fixture's own shell. `RailUnavailable` stands in, and the page
 *   stays.
 * - The whole app (`main.tsx`), for what nothing else caught: the shell
 *   itself, a provider. `AppUnavailable` offers a reload.
 *
 * **It tries again when what the part is drawn from changes** (`retryKey`):
 * the next status a fixture's poll hands down, the property a fixture reads
 * for, the properties the notice names, the page the rail is on. The fault is
 * most likely in that one answer, so the next may well draw, and the part is
 * then back without a reload.
 * Rendering again under the same key tries nothing, since it would throw the
 * same way, and a key that failed as it arrived is not tried a second time. A
 * failed try is not seen: React swaps the fallback back in before it paints.
 * With no key, nothing is tried again: a page is left for another one, and the
 * whole app is reloaded.
 *
 * **Each fault is reported once a page load, under the part it cost.** A
 * caught error no longer reaches the global handler, so it is reported from
 * here, or a rail that reads "Status unknown" for every user would look like a
 * healthy deploy. Once, not once a catch. A boundary that tries again with
 * every poll would send a report a minute from every open tab. And the rail is
 * drawn twice, beside the page and in the phone's menu, the menu afresh each
 * time it opens, so a record kept on the boundary would report once a copy and
 * again with every opening. The record is the module's, keyed by the part and
 * the fault (its name and message), and lives as long as the page load. The
 * same fault under another part is another thing the user lost, and is
 * reported again.
 *
 * It contains what is thrown while rendering or committing. A handler that
 * throws on a click never unmounted anything, and still reaches the global
 * handler as before.
 */

interface RenderFaultBoundaryProps {
    children: ReactNode;
    /**
     * What is contained, as its report names it: `rail.syncStatus`, `page`,
     * `app`. The report's `boundary` tag, so a fault says what the user lost.
     */
    part: string;
    /**
     * Shown where the children stood, for as long as they cannot be drawn.
     * Null where the part already shows nothing when its answer cannot be
     * read. It must read nothing the children were drawn from, or it fails
     * the way they did and takes the next boundary out with it.
     */
    fallback: ReactNode;
    /**
     * What the children are drawn from. A new one is a reason to try again.
     * Where the boundary sits under the answer, the answer itself; where the
     * part reads its own, what makes it read again.
     */
    retryKey?: unknown;
    /**
     * Told of every catch, reported or not, for an owner with something of its
     * own to settle: a window that was asked for and did not open.
     */
    onFault?: () => void;
}

interface RenderFaultBoundaryState {
    failed: boolean;
}

/** One fault, however many times it is thrown: its name and what it says. */
function faultOf(error: unknown): string {
    return error instanceof Error ? `${error.name}: ${error.message}` : String(error);
}

/** What has been reported since the page loaded, by part and fault. */
const reported = new Set<string>();

/**
 * Forgets what has been reported, so the next catch of a fault reports it
 * again. A page load starts with nothing reported and never asks; a test that
 * counts reports does, since the tests of one file share the module.
 */
export function forgetReportedFaults(): void {
    reported.clear();
}

export class RenderFaultBoundary extends Component<RenderFaultBoundaryProps, RenderFaultBoundaryState> {
    state: RenderFaultBoundaryState = { failed: false };

    static getDerivedStateFromError(): RenderFaultBoundaryState {
        return { failed: true };
    }

    componentDidCatch(error: unknown, info: ErrorInfo): void {
        const { part, onFault } = this.props;
        const report = `${part}\n${faultOf(error)}`;
        if (!reported.has(report)) {
            reported.add(report);
            captureException(error, {
                tags: { boundary: part },
                extra: { componentStack: info.componentStack },
            });
        }
        onFault?.();
    }

    componentDidUpdate(previousProps: RenderFaultBoundaryProps, previousState: RenderFaultBoundaryState): void {
        // Only a fault that was already standing is tried again. One thrown in
        // this very update came from what changed the key, and would be thrown
        // again by it.
        if (this.state.failed && previousState.failed && previousProps.retryKey !== this.props.retryKey) {
            this.setState({ failed: false });
        }
    }

    render(): ReactNode {
        return this.state.failed ? this.props.fallback : this.props.children;
    }
}
